// src/pages/club/ClubMembers.tsx
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Users,
  Mail,
  Search,
  Plus,
  Trash2,
  Copy,
  CheckCircle2,
  Clock,
  RefreshCw,
  Shield,
  Upload,
  Download,
  FileText,
  X,
  AlertTriangle,
  Loader2,
  Send,
} from "lucide-react";
import { supabase } from "../supabaseClient";

type ClubRole =
  | "dirigeant"
  | "cadre_technique"
  | "coach"
  | "joueur"
  | "lecteur";

type MemberStatus = "active" | "pending";

type ClubMemberRow = {
  id: string;
  club_id: string;
  user_id: string | null;
  email: string | null;
  role: string;
  status: string;
  created_at: string | null;
  accepted_at: string | null;
};

type ClubInvitationRow = {
  id: string;
  club_id: string;
  email: string;
  role: string;
  token: string;
  invited_by: string | null;
  created_at: string;
  accepted_at: string | null;
  expires_at: string | null;
};

type ListItem = {
  id: string;
  source: "member" | "invitation";
  name: string;
  email: string;
  role: ClubRole;
  status: MemberStatus;
  createdAt: string;
  token?: string;
  userId?: string | null;
};

function cx(...classes: Array<string | false | undefined | null>) {
  return classes.filter(Boolean).join(" ");
}

const ROLE_LABEL: Record<ClubRole, string> = {
  dirigeant: "Dirigeant",
  cadre_technique: "Cadre technique",
  coach: "Coach",
  joueur: "Joueur",
  lecteur: "Lecteur",
};

function roleBadgeClass(role: ClubRole) {
  switch (role) {
    case "dirigeant":
      return "bg-yellow-100 text-yellow-900 dark:bg-yellow-900/20 dark:text-yellow-200";
    case "cadre_technique":
      return "bg-purple-100 text-purple-900 dark:bg-purple-900/20 dark:text-purple-200";
    case "coach":
      return "bg-blue-100 text-blue-900 dark:bg-blue-900/20 dark:text-blue-200";
    case "joueur":
      return "bg-emerald-100 text-emerald-900 dark:bg-emerald-900/20 dark:text-emerald-200";
    case "lecteur":
      return "bg-gray-100 text-gray-900 dark:bg-gray-800 dark:text-gray-200";
  }
}

function formatShortDate(iso?: string | null) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("fr-FR", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function getFallbackNameFromEmail(email?: string | null) {
  if (!email) return "Membre";
  const raw = email.split("@")[0] || "membre";
  return raw
    .replace(/[._-]+/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * Mapping UI FR <-> DB invitation role (table club_invitations.role)
 * DB attend ici :
 * - admin
 * - assistant
 * - coach
 * - player
 * - viewer
 */
const uiRoleToInvitationRole: Record<ClubRole, string> = {
  dirigeant: "admin",
  cadre_technique: "assistant",
  coach: "coach",
  joueur: "joueur",
  lecteur: "viewer",
};

function invitationRoleToUiRole(dbRole: string): ClubRole | null {
  switch (dbRole) {
    case "admin":
      return "dirigeant";
    case "assistant":
      return "cadre_technique";
    case "coach":
      return "coach";
    case "joueur":
      return "joueur";
    case "viewer":
      return "lecteur";
    default:
      return null;
  }
}

function memberRoleToUiRole(dbRole: string): ClubRole | null {
  switch (dbRole) {
    case "dirigeant":
      return "dirigeant";
    case "cadre_technique":
      return "cadre_technique";
    case "coach":
      return "coach";
    case "joueur":
      return "joueur";
    case "lecteur":
      return "lecteur";
    default:
      return null;
  }
}

function makeInviteToken() {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID().replace(/-/g, "");
  }
  return `${Date.now()}_${Math.random().toString(36).slice(2, 14)}`;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Parse un texte CSV/TSV (virgule, point-virgule ou tabulation) en tableau de lignes/cellules. */
function parseDelimitedText(text: string): string[][] {
  const cleaned = text.replace(/^﻿/, "").replace(/\r\n/g, "\n").replace(/\r/g, "\n").trim();
  if (!cleaned) return [];
  const lines = cleaned.split("\n").filter((l) => l.trim().length > 0);
  const first = lines[0];
  const delimiter = first.includes(";") && !first.includes(",") ? ";" : first.includes("\t") ? "\t" : ",";

  const parseLine = (line: string): string[] => {
    const cells: string[] = [];
    let cur = "";
    let inQuotes = false;
    for (let i = 0; i < line.length; i++) {
      const c = line[i];
      if (inQuotes) {
        if (c === '"') {
          if (line[i + 1] === '"') {
            cur += '"';
            i++;
          } else {
            inQuotes = false;
          }
        } else {
          cur += c;
        }
      } else if (c === '"') {
        inQuotes = true;
      } else if (c === delimiter) {
        cells.push(cur.trim());
        cur = "";
      } else {
        cur += c;
      }
    }
    cells.push(cur.trim());
    return cells;
  };

  return lines.map(parseLine);
}

/** Extrait { email, role } de lignes CSV, avec ou sans en-tête. */
function extractRowsFromCsv(text: string): { email: string; role?: string }[] {
  const rows = parseDelimitedText(text);
  if (rows.length === 0) return [];

  let dataRows = rows;
  let emailIdx = 0;
  let roleIdx = -1;

  const headerCandidate = rows[0].map((c) => c.toLowerCase());
  const looksLikeHeader = headerCandidate.some((c) => c.includes("email") || c.includes("mail"));

  if (looksLikeHeader) {
    emailIdx = headerCandidate.findIndex((c) => c.includes("email") || c.includes("mail"));
    roleIdx = headerCandidate.findIndex((c) => c.includes("role") || c.includes("rôle"));
    dataRows = rows.slice(1);
  } else if (rows[0].length > 1) {
    roleIdx = 1;
  }

  return dataRows
    .map((r) => ({
      email: (r[emailIdx] || "").trim(),
      role: roleIdx >= 0 ? (r[roleIdx] || "").trim() : undefined,
    }))
    .filter((r) => r.email.length > 0);
}

/** Normalise un libellé de rôle libre (FR/EN, avec ou sans accents) vers un ClubRole connu. */
function normalizeRoleToken(raw?: string): ClubRole | null {
  if (!raw) return null;
  const DIACRITICS_RE = new RegExp("[\\u0300-\\u036f]", "g");
  const s = raw
    .toLowerCase()
    .normalize("NFD")
    .replace(DIACRITICS_RE, "")
    .trim()
    .replace(/[\s-]+/g, "_");

  const aliases: Record<string, ClubRole> = {
    dirigeant: "dirigeant",
    dirigeants: "dirigeant",
    admin: "dirigeant",
    administrateur: "dirigeant",
    cadre_technique: "cadre_technique",
    cadretechnique: "cadre_technique",
    assistant: "cadre_technique",
    coach: "coach",
    coachs: "coach",
    entraineur: "coach",
    joueur: "joueur",
    joueurs: "joueur",
    joueuse: "joueur",
    player: "joueur",
    lecteur: "lecteur",
    lecteurs: "lecteur",
    viewer: "lecteur",
    spectateur: "lecteur",
  };

  return aliases[s] ?? null;
}

type BulkIssue =
  | "invalid_email"
  | "duplicate_in_file"
  | "already_member"
  | "already_invited"
  | "role_not_allowed"
  | null;

type BulkRow = {
  rowIndex: number;
  email: string;
  role: ClubRole;
  roleExplicit: boolean;
  issue: BulkIssue;
};

const BULK_ISSUE_LABEL: Record<Exclude<BulkIssue, null>, string> = {
  invalid_email: "Email invalide",
  duplicate_in_file: "Doublon dans le fichier",
  already_member: "Déjà membre du club",
  already_invited: "Invitation déjà en attente",
  role_not_allowed: "Rôle non autorisé pour toi",
};

type BulkSendStatus = "pending" | "sending" | "sent" | "error";

export default function ClubMembers() {
  const { clubId } = useParams<{ clubId: string }>();

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const [myRoles, setMyRoles] = useState<ClubRole[]>([]);
  const [myUserId, setMyUserId] = useState<string | null>(null);
  const [members, setMembers] = useState<ClubMemberRow[]>([]);
  const [invitations, setInvitations] = useState<ClubInvitationRow[]>([]);

  const [email, setEmail] = useState("");
  const [selectedRole, setSelectedRole] = useState<ClubRole>("coach");
  const [saving, setSaving] = useState(false);

  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState<"all" | MemberStatus>("all");
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  // --- invitation en masse (CSV) ---
  const [showBulkImport, setShowBulkImport] = useState(false);
  const [bulkDefaultRole, setBulkDefaultRole] = useState<ClubRole>("joueur");
  const [rawCsvRows, setRawCsvRows] = useState<{ email: string; role?: string }[]>([]);
  const [csvFileName, setCsvFileName] = useState<string | null>(null);
  const [csvParseError, setCsvParseError] = useState<string | null>(null);
  const [excludedRows, setExcludedRows] = useState<Set<number>>(new Set());
  const [bulkSending, setBulkSending] = useState(false);
  const [bulkStatus, setBulkStatus] = useState<Record<number, BulkSendStatus>>({});
  const [bulkErrorDetails, setBulkErrorDetails] = useState<Record<number, string>>({});
  const [bulkSummary, setBulkSummary] = useState<{ sent: number; failed: number } | null>(null);

  const isDirigeant = myRoles.includes("dirigeant");
  const isCadreTechnique = myRoles.includes("cadre_technique");

  const allowedInviteRoles = useMemo<ClubRole[]>(() => {
    if (isDirigeant) {
      return ["dirigeant", "cadre_technique", "coach", "joueur", "lecteur"];
    }
    if (isCadreTechnique) {
      return ["coach", "joueur", "lecteur"];
    }
    return [];
  }, [isDirigeant, isCadreTechnique]);

  const allowedRemoveRoles = useMemo<ClubRole[]>(() => {
    if (isDirigeant) {
      return ["dirigeant", "cadre_technique", "coach", "joueur", "lecteur"];
    }
    if (isCadreTechnique) {
      return ["coach", "joueur", "lecteur"];
    }
    return [];
  }, [isDirigeant, isCadreTechnique]);

  const canManage = allowedInviteRoles.length > 0;

  const bulkRows = useMemo<BulkRow[]>(() => {
    const seen = new Set<string>();

    return rawCsvRows.map((r, idx) => {
      const email = r.email.trim().toLowerCase();
      const explicitRole = normalizeRoleToken(r.role);
      const role = explicitRole ?? bulkDefaultRole;
      let issue: BulkIssue = null;

      if (!EMAIL_RE.test(email)) {
        issue = "invalid_email";
      } else if (seen.has(email)) {
        issue = "duplicate_in_file";
      } else if (!allowedInviteRoles.includes(role)) {
        issue = "role_not_allowed";
      } else if (
        members.some((m) => m.status === "active" && (m.email || "").toLowerCase() === email)
      ) {
        issue = "already_member";
      } else if (
        invitations.some(
          (inv) =>
            inv.email.toLowerCase() === email &&
            invitationRoleToUiRole(inv.role) === role &&
            !inv.accepted_at
        )
      ) {
        issue = "already_invited";
      }

      if (!issue) seen.add(email);

      return { rowIndex: idx, email, role, roleExplicit: !!explicitRole, issue };
    });
  }, [rawCsvRows, bulkDefaultRole, members, invitations, allowedInviteRoles]);

  const bulkSelectableRows = useMemo(
    () => bulkRows.filter((r) => !r.issue),
    [bulkRows]
  );

  const bulkRowsToSend = useMemo(
    () => bulkSelectableRows.filter((r) => !excludedRows.has(r.rowIndex)),
    [bulkSelectableRows, excludedRows]
  );

  const canRemoveItem = useCallback(
    (item: ListItem) => {
      if (!allowedRemoveRoles.includes(item.role)) return false;

      if (item.source === "member" && item.userId && myUserId && item.userId === myUserId) {
        return false;
      }

      return true;
    },
    [allowedRemoveRoles, myUserId]
  );

  const loadData = useCallback(async () => {
    if (!clubId) return;

    setErrorMsg(null);

    const {
      data: { user },
      error: authError,
    } = await supabase.auth.getUser();

    if (authError) throw authError;
    if (!user) throw new Error("Utilisateur non connecté.");

    setMyUserId(user.id);

    const [membersRes, invitationsRes] = await Promise.all([
      supabase
        .from("club_members")
        .select("id, club_id, user_id, email, role, status, created_at, accepted_at")
        .eq("club_id", clubId)
        .order("created_at", { ascending: false }),
      supabase
        .from("club_invitations")
        .select("id, club_id, email, role, token, invited_by, created_at, accepted_at, expires_at")
        .eq("club_id", clubId)
        .is("accepted_at", null)
        .order("created_at", { ascending: false }),
    ]);

    if (membersRes.error) throw membersRes.error;
    if (invitationsRes.error) throw invitationsRes.error;

    const memberRows = (membersRes.data ?? []) as ClubMemberRow[];
    const invitationRows = (invitationsRes.data ?? []) as ClubInvitationRow[];

    setMembers(memberRows);
    setInvitations(invitationRows);

    const myRows = memberRows.filter(
      (m) => m.user_id === user.id && m.status === "active"
    );

    const roles = myRows
      .map((m) => memberRoleToUiRole(m.role))
      .filter(Boolean) as ClubRole[];

    setMyRoles(Array.from(new Set(roles)));
  }, [clubId]);

  useEffect(() => {
    let cancelled = false;

    async function run() {
      if (!clubId) {
        setLoading(false);
        setErrorMsg("Club introuvable.");
        return;
      }

      try {
        setLoading(true);
        await loadData();
      } catch (err: any) {
        if (!cancelled) {
          setErrorMsg(err?.message || "Impossible de charger les membres du club.");
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    run();

    return () => {
      cancelled = true;
    };
  }, [clubId, loadData]);

  async function handleRefresh() {
    try {
      setRefreshing(true);
      await loadData();
    } catch (err: any) {
      setErrorMsg(err?.message || "Impossible d’actualiser les données.");
    } finally {
      setRefreshing(false);
    }
  }

  const listItems = useMemo<ListItem[]>(() => {
    const memberItems: ListItem[] = members
      .map((m) => {
        const role = memberRoleToUiRole(m.role);
        if (!role) return null;

        return {
          id: m.id,
          source: "member" as const,
          name: getFallbackNameFromEmail(m.email),
          email: m.email || "—",
          role,
          status: "active" as const,
          createdAt: m.created_at || new Date().toISOString(),
          userId: m.user_id,
        };
      })
      .filter(Boolean) as ListItem[];

    const invitationItems: ListItem[] = invitations
      .map((inv) => {
        const role = invitationRoleToUiRole(inv.role);
        if (!role) return null;

        return {
          id: inv.id,
          source: "invitation" as const,
          name: getFallbackNameFromEmail(inv.email),
          email: inv.email,
          role,
          status: "pending" as const,
          createdAt: inv.created_at,
          token: inv.token,
        };
      })
      .filter(Boolean) as ListItem[];

    return [...invitationItems, ...memberItems].sort((a, b) => {
      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
    });
  }, [members, invitations]);

  const filtered = useMemo(() => {
    const qq = q.trim().toLowerCase();

    return listItems.filter((item) => {
      const matchQ =
        !qq ||
        item.name.toLowerCase().includes(qq) ||
        item.email.toLowerCase().includes(qq) ||
        ROLE_LABEL[item.role].toLowerCase().includes(qq);

      const matchStatus = statusFilter === "all" ? true : item.status === statusFilter;
      return matchQ && matchStatus;
    });
  }, [listItems, q, statusFilter]);

  async function handleInvite(e: React.FormEvent) {
    e.preventDefault();
    if (!clubId || !canManage) return;

    const cleanEmail = email.trim().toLowerCase();
    if (!cleanEmail) return;

    if (!allowedInviteRoles.includes(selectedRole)) {
      setErrorMsg("Tu n’as pas le droit d’inviter ce rôle.");
      return;
    }

    setSaving(true);
    setErrorMsg(null);

    try {
      const {
        data: { user },
        error: authError,
      } = await supabase.auth.getUser();

      if (authError) throw authError;
      if (!user) throw new Error("Utilisateur non connecté.");

      const existingPending = invitations.find(
        (inv) =>
          inv.email.toLowerCase() === cleanEmail &&
          invitationRoleToUiRole(inv.role) === selectedRole &&
          !inv.accepted_at
      );

      if (existingPending) {
        throw new Error("Une invitation en attente existe déjà pour cet email et ce rôle.");
      }

      const dbRole = uiRoleToInvitationRole[selectedRole];
      const token = makeInviteToken();

      const { error } = await supabase.from("club_invitations").insert({
        club_id: clubId,
        email: cleanEmail,
        role: dbRole,
        token,
        invited_by: user.id,
      });

      if (error) throw error;

      setEmail("");
      setSelectedRole(allowedInviteRoles[0] ?? "coach");
      await loadData();
    } catch (err: any) {
      setErrorMsg(err?.message || "Impossible d’envoyer l’invitation.");
    } finally {
      setSaving(false);
    }
  }

  async function handleRemove(item: ListItem) {
    if (!canRemoveItem(item)) {
      setErrorMsg("Tu n’as pas le droit de supprimer ce membre ou cette invitation.");
      return;
    }

    const confirmLabel =
      item.source === "invitation"
        ? `Supprimer l’invitation de ${item.email} ?`
        : `Retirer le membre ${item.email} du club ?`;

    if (!window.confirm(confirmLabel)) return;

    setErrorMsg(null);

    try {
      if (item.source === "invitation") {
        const { error } = await supabase
          .from("club_invitations")
          .delete()
          .eq("id", item.id);

        if (error) throw error;
      } else {
        const { error } = await supabase
          .from("club_members")
          .delete()
          .eq("id", item.id);

        if (error) throw error;
      }

      await loadData();
    } catch (err: any) {
      setErrorMsg(err?.message || "Impossible de supprimer cet élément.");
    }
  }

  async function handleCopyInviteLink(item: ListItem) {
    if (item.source !== "invitation" || !item.token) return;

    const inviteLink = `${window.location.origin}/invite/${item.token}`;

    try {
      await navigator.clipboard.writeText(inviteLink);
      window.alert("Lien d’invitation copié.");
    } catch {
      window.alert("Impossible de copier le lien.");
    }
  }

  function downloadCsvTemplate() {
    const header = "email,role\n";
    const example = [
      "joueur1@monclub.com,joueur",
      "coach1@monclub.com,coach",
      "dirigeant1@monclub.com,dirigeant",
    ].join("\n");
    const blob = new Blob(["﻿" + header + example], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "modele_invitations_club.csv";
    a.click();
    URL.revokeObjectURL(url);
  }

  function resetBulkImport() {
    setRawCsvRows([]);
    setCsvFileName(null);
    setCsvParseError(null);
    setExcludedRows(new Set());
    setBulkStatus({});
    setBulkErrorDetails({});
    setBulkSummary(null);
  }

  function handleBulkFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;

    resetBulkImport();

    const reader = new FileReader();
    reader.onload = () => {
      try {
        const text = String(reader.result || "");
        const rows = extractRowsFromCsv(text);

        if (rows.length === 0) {
          setCsvParseError("Aucune ligne exploitable trouvée dans ce fichier.");
          return;
        }

        setRawCsvRows(rows);
        setCsvFileName(file.name);
      } catch (err: any) {
        setCsvParseError(err?.message || "Impossible de lire ce fichier.");
      }
    };
    reader.onerror = () => setCsvParseError("Impossible de lire ce fichier.");
    reader.readAsText(file, "utf-8");
  }

  function toggleRowExcluded(rowIndex: number) {
    setExcludedRows((prev) => {
      const next = new Set(prev);
      if (next.has(rowIndex)) next.delete(rowIndex);
      else next.add(rowIndex);
      return next;
    });
  }

  async function handleSendBulkInvites() {
    if (!clubId || bulkRowsToSend.length === 0 || bulkSending) return;

    setBulkSending(true);
    setBulkSummary(null);
    setErrorMsg(null);
    setSuccessMsg(null);

    const {
      data: { user },
      error: authError,
    } = await supabase.auth.getUser();

    if (authError || !user) {
      setErrorMsg("Utilisateur non connecté.");
      setBulkSending(false);
      return;
    }

    const toSend = bulkRowsToSend;
    setBulkStatus((prev) => {
      const next = { ...prev };
      toSend.forEach((r) => {
        next[r.rowIndex] = "pending";
      });
      return next;
    });

    let sent = 0;
    let failed = 0;
    const CHUNK_SIZE = 5;

    for (let i = 0; i < toSend.length; i += CHUNK_SIZE) {
      const chunk = toSend.slice(i, i + CHUNK_SIZE);

      setBulkStatus((prev) => {
        const next = { ...prev };
        chunk.forEach((r) => {
          next[r.rowIndex] = "sending";
        });
        return next;
      });

      const results = await Promise.allSettled(
        chunk.map((r) =>
          supabase.from("club_invitations").insert({
            club_id: clubId,
            email: r.email,
            role: uiRoleToInvitationRole[r.role],
            token: makeInviteToken(),
            invited_by: user.id,
          })
        )
      );

      const errorsThisChunk: Record<number, string> = {};

      for (let j = 0; j < results.length; j++) {
        const res = results[j];
        const row = chunk[j];
        const supaError = res.status === "fulfilled" ? (res.value as any).error : res.reason;

        if (supaError) {
          failed += 1;
          errorsThisChunk[row.rowIndex] = supaError?.message || "Erreur inconnue";
        } else {
          sent += 1;
        }
      }

      setBulkStatus((prev) => {
        const next = { ...prev };
        chunk.forEach((r) => {
          next[r.rowIndex] = errorsThisChunk[r.rowIndex] ? "error" : "sent";
        });
        return next;
      });

      if (Object.keys(errorsThisChunk).length > 0) {
        setBulkErrorDetails((prev) => ({ ...prev, ...errorsThisChunk }));
      }
    }

    setBulkSummary({ sent, failed });
    setBulkSending(false);

    if (sent > 0) {
      setSuccessMsg(
        `${sent} invitation(s) envoyée(s)${failed > 0 ? `, ${failed} échec(s)` : ""}.`
      );
      await loadData();
    } else if (failed > 0) {
      setErrorMsg(`Aucune invitation envoyée, ${failed} échec(s). Vérifie le détail des lignes.`);
    }
  }

  if (loading) {
    return (
      <div className="rounded-2xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 p-6 text-sm text-gray-500 dark:text-gray-400">
        Chargement des membres du club…
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col items-center text-center gap-3 sm:flex-row sm:items-start sm:justify-between sm:text-left">
        <div>
          <div className="text-sm text-gray-500 dark:text-gray-400">Gestion</div>
          <div className="text-xl font-semibold text-gray-900 dark:text-gray-100">
            Membres du club
          </div>
          <div className="text-sm text-gray-500 dark:text-gray-400">
            Invite des dirigeants, cadres techniques, coachs, joueurs et lecteurs.
          </div>
        </div>

        <button
          type="button"
          onClick={handleRefresh}
          className="inline-flex items-center gap-2 px-3 py-2 rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 text-sm text-gray-900 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-800"
          disabled={refreshing}
        >
          <RefreshCw className={cx("h-4 w-4", refreshing && "animate-spin")} />
          Actualiser
        </button>
      </div>

      {errorMsg && (
        <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-900/40 dark:bg-red-900/10 dark:text-red-200">
          {errorMsg}
        </div>
      )}

      {successMsg && (
        <div className="rounded-2xl border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-700 dark:border-green-900/40 dark:bg-green-900/10 dark:text-green-200">
          {successMsg}
        </div>
      )}

      <div className="rounded-2xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 p-4">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <Users className="h-4 w-4 text-gray-500" />
            <div className="font-semibold text-gray-900 dark:text-gray-100">
              Inviter un membre
            </div>
          </div>

          {canManage && (
            <button
              type="button"
              onClick={() => setShowBulkImport((v) => !v)}
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 text-xs font-medium text-gray-900 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-800"
            >
              <Upload className="h-3.5 w-3.5" />
              {showBulkImport ? "Masquer l’import CSV" : "Invitation en masse (CSV)"}
            </button>
          )}
        </div>

        {!canManage && (
          <div className="mt-3 text-sm text-red-600 dark:text-red-300">
            Tu n’as pas les droits pour inviter des membres.
          </div>
        )}

        {canManage && (
          <div className="mt-3 flex flex-wrap gap-2 text-xs text-gray-500 dark:text-gray-400">
            <span className="inline-flex items-center gap-1 rounded-full bg-gray-100 dark:bg-gray-800 px-2 py-1">
              <Shield className="h-3.5 w-3.5" />
              Tes rôles :
            </span>
            {myRoles.map((role) => (
              <span
                key={role}
                className={cx("inline-flex rounded-full px-2 py-1", roleBadgeClass(role))}
              >
                {ROLE_LABEL[role]}
              </span>
            ))}
          </div>
        )}

        <form onSubmit={handleInvite} className="mt-4 grid grid-cols-1 lg:grid-cols-12 gap-3">
          <div className="lg:col-span-6">
            <label className="text-xs text-gray-500 dark:text-gray-400">Email</label>
            <div className="mt-1 relative">
              <Mail className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="membre@mail.com"
                className="w-full pl-9 pr-3 py-2 rounded-xl bg-gray-50 dark:bg-gray-950 border border-gray-200 dark:border-gray-800 text-sm text-gray-900 dark:text-gray-100 outline-none focus:ring-2 focus:ring-gray-300 dark:focus:ring-gray-700"
                disabled={!canManage || saving}
                type="email"
                required
              />
            </div>
          </div>

          <div className="lg:col-span-4">
            <label className="text-xs text-gray-500 dark:text-gray-400">Rôle</label>
            <div className="mt-1 flex flex-wrap gap-2">
              {(
                ["dirigeant", "cadre_technique", "coach", "joueur", "lecteur"] as ClubRole[]
              ).map((role) => {
                const disabled = !allowedInviteRoles.includes(role) || !canManage || saving;

                return (
                  <button
                    key={role}
                    type="button"
                    onClick={() => !disabled && setSelectedRole(role)}
                    disabled={disabled}
                    className={cx(
                      "text-xs px-3 py-2 rounded-full border transition",
                      selectedRole === role
                        ? "bg-gray-900 text-white border-gray-900 dark:bg-white dark:text-gray-900 dark:border-white"
                        : "bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 border-gray-200 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800",
                      disabled && "opacity-50 cursor-not-allowed"
                    )}
                  >
                    {ROLE_LABEL[role]}
                  </button>
                );
              })}
            </div>
            <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Dirigeant : invite tout. Cadre technique : coach, joueur et lecteur.
            </div>
          </div>

          <div className="lg:col-span-2 flex items-end">
            <button
              type="submit"
              disabled={!canManage || saving || !email.trim()}
              className={cx(
                "w-full inline-flex items-center justify-center gap-2 px-3 py-2 rounded-xl text-sm",
                "bg-gray-900 text-white hover:bg-black dark:bg-white dark:text-gray-900 dark:hover:bg-gray-100",
                (!canManage || saving || !email.trim()) && "opacity-60 cursor-not-allowed"
              )}
            >
              <Plus className="h-4 w-4" />
              {saving ? "..." : "Inviter"}
            </button>
          </div>
        </form>
      </div>

      {canManage && showBulkImport && (
        <div className="rounded-2xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 p-4">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <Upload className="h-4 w-4 text-gray-500" />
              <div className="font-semibold text-gray-900 dark:text-gray-100">
                Invitation en masse (CSV)
              </div>
            </div>
            <button
              type="button"
              onClick={() => setShowBulkImport(false)}
              className="p-1.5 rounded-lg text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800"
              aria-label="Fermer l’import CSV"
              title="Fermer"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          <div className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Importe un fichier CSV avec les colonnes <span className="font-mono">email</span> et{" "}
            <span className="font-mono">role</span> (optionnelle). Sans colonne rôle, le rôle par
            défaut ci-dessous est appliqué à toutes les lignes.
          </div>

          <div className="mt-4 flex flex-wrap items-end gap-3">
            <div>
              <label className="text-xs text-gray-500 dark:text-gray-400">Rôle par défaut</label>
              <div className="mt-1 flex flex-wrap gap-2">
                {(
                  ["dirigeant", "cadre_technique", "coach", "joueur", "lecteur"] as ClubRole[]
                ).map((role) => {
                  const disabled = !allowedInviteRoles.includes(role);
                  return (
                    <button
                      key={role}
                      type="button"
                      onClick={() => !disabled && setBulkDefaultRole(role)}
                      disabled={disabled}
                      className={cx(
                        "text-xs px-3 py-2 rounded-full border transition",
                        bulkDefaultRole === role
                          ? "bg-gray-900 text-white border-gray-900 dark:bg-white dark:text-gray-900 dark:border-white"
                          : "bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 border-gray-200 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800",
                        disabled && "opacity-50 cursor-not-allowed"
                      )}
                    >
                      {ROLE_LABEL[role]}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="flex items-center gap-2 ml-auto">
              <button
                type="button"
                onClick={downloadCsvTemplate}
                className="inline-flex items-center gap-2 px-3 py-2 rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 text-sm text-gray-900 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-800"
              >
                <Download className="h-4 w-4" />
                Modèle CSV
              </button>

              <label className="inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-gray-900 text-white hover:bg-black dark:bg-white dark:text-gray-900 dark:hover:bg-gray-100 text-sm cursor-pointer">
                <Upload className="h-4 w-4" />
                Choisir un fichier CSV
                <input type="file" accept=".csv,text/csv" className="hidden" onChange={handleBulkFileChange} />
              </label>
            </div>
          </div>

          {csvFileName && (
            <div className="mt-3 flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
              <FileText className="h-4 w-4" />
              {csvFileName}
              <button
                type="button"
                onClick={resetBulkImport}
                className="text-xs text-gray-400 hover:text-red-600 dark:hover:text-red-300 underline"
              >
                retirer
              </button>
            </div>
          )}

          {csvParseError && (
            <div className="mt-3 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-900/40 dark:bg-red-900/10 dark:text-red-200">
              {csvParseError}
            </div>
          )}

          {bulkRows.length > 0 && (
            <>
              <div className="mt-4 flex flex-wrap items-center gap-3 text-xs text-gray-500 dark:text-gray-400">
                <span className="inline-flex items-center gap-1 rounded-full bg-green-100 text-green-900 dark:bg-green-900/20 dark:text-green-200 px-2 py-1">
                  {bulkRowsToSend.length} prête(s) à être envoyée(s)
                </span>
                {bulkRows.length - bulkSelectableRows.length > 0 && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-yellow-100 text-yellow-900 dark:bg-yellow-900/20 dark:text-yellow-200 px-2 py-1">
                    <AlertTriangle className="h-3.5 w-3.5" />
                    {bulkRows.length - bulkSelectableRows.length} ignorée(s)
                  </span>
                )}
                <span>{bulkRows.length} ligne(s) au total</span>
              </div>

              <div className="mt-3 rounded-xl border border-gray-200 dark:border-gray-800 overflow-hidden">
                <div className="max-h-80 overflow-y-auto divide-y divide-gray-200 dark:divide-gray-800">
                  {bulkRows.map((row) => {
                    const status = bulkStatus[row.rowIndex];
                    const excluded = excludedRows.has(row.rowIndex);
                    const checked = !row.issue && !excluded;

                    return (
                      <div
                        key={row.rowIndex}
                        className="px-3 py-2 flex items-center gap-3 text-sm"
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          disabled={!!row.issue || bulkSending}
                          onChange={() => toggleRowExcluded(row.rowIndex)}
                          className="h-4 w-4"
                        />

                        <div className="flex-1 min-w-0 truncate text-gray-900 dark:text-gray-100">
                          {row.email || <span className="italic text-gray-400">(vide)</span>}
                        </div>

                        <span className={cx("text-xs px-2 py-1 rounded-full shrink-0", roleBadgeClass(row.role))}>
                          {ROLE_LABEL[row.role]}
                        </span>

                        {row.issue ? (
                          <span
                            className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-200 shrink-0"
                            title={bulkErrorDetails[row.rowIndex]}
                          >
                            <AlertTriangle className="h-3.5 w-3.5" />
                            {BULK_ISSUE_LABEL[row.issue]}
                          </span>
                        ) : status === "sent" ? (
                          <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-900 dark:bg-green-900/20 dark:text-green-200 shrink-0">
                            <CheckCircle2 className="h-3.5 w-3.5" /> Envoyée
                          </span>
                        ) : status === "error" ? (
                          <span
                            className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-200 shrink-0"
                            title={bulkErrorDetails[row.rowIndex]}
                          >
                            <AlertTriangle className="h-3.5 w-3.5" /> Échec
                          </span>
                        ) : status === "sending" ? (
                          <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-200 shrink-0">
                            <Loader2 className="h-3.5 w-3.5 animate-spin" /> Envoi…
                          </span>
                        ) : (
                          <span className="text-xs text-gray-400 shrink-0">
                            {excluded ? "Ignorée" : "En attente"}
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              {bulkSummary && (
                <div
                  className={cx(
                    "mt-3 rounded-xl px-3 py-2 text-sm",
                    bulkSummary.failed > 0
                      ? "bg-yellow-50 text-yellow-800 dark:bg-yellow-900/10 dark:text-yellow-200"
                      : "bg-green-50 text-green-800 dark:bg-green-900/10 dark:text-green-200"
                  )}
                >
                  {bulkSummary.sent} invitation(s) envoyée(s)
                  {bulkSummary.failed > 0 ? `, ${bulkSummary.failed} échec(s).` : "."}
                </div>
              )}

              <div className="mt-4 flex items-center gap-3">
                <button
                  type="button"
                  onClick={handleSendBulkInvites}
                  disabled={bulkRowsToSend.length === 0 || bulkSending}
                  className={cx(
                    "inline-flex items-center justify-center gap-2 px-4 py-2 rounded-xl text-sm",
                    "bg-gray-900 text-white hover:bg-black dark:bg-white dark:text-gray-900 dark:hover:bg-gray-100",
                    (bulkRowsToSend.length === 0 || bulkSending) && "opacity-60 cursor-not-allowed"
                  )}
                >
                  {bulkSending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                  {bulkSending
                    ? "Envoi en cours…"
                    : `Envoyer ${bulkRowsToSend.length} invitation(s)`}
                </button>

                <button
                  type="button"
                  onClick={resetBulkImport}
                  disabled={bulkSending}
                  className="text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 underline disabled:opacity-50"
                >
                  Effacer
                </button>
              </div>
            </>
          )}
        </div>
      )}

      <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
        <div className="relative w-full sm:max-w-md">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Rechercher un membre, email, rôle…"
            className="w-full pl-9 pr-3 py-2 rounded-xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 text-sm text-gray-900 dark:text-gray-100 outline-none focus:ring-2 focus:ring-gray-300 dark:focus:ring-gray-700"
          />
        </div>

        <div className="flex gap-2">
          {(["all", "active", "pending"] as const).map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => setStatusFilter(s)}
              className={cx(
                "px-3 py-2 rounded-xl text-sm border",
                statusFilter === s
                  ? "bg-gray-100 text-gray-900 border-gray-200 dark:bg-gray-800 dark:text-gray-100 dark:border-gray-700"
                  : "bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-200 border-gray-200 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800"
              )}
            >
              {s === "all" ? "Tous" : s === "active" ? "Actifs" : "En attente"}
            </button>
          ))}
        </div>
      </div>

      <div className="rounded-2xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-800 flex items-center justify-between">
          <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">
            {filtered.length} élément(s)
          </div>
          <div className="text-xs text-gray-500 dark:text-gray-400">
            Club ID : <span className="font-mono">{clubId}</span>
          </div>
        </div>

        <div className="divide-y divide-gray-200 dark:divide-gray-800">
          {filtered.map((item) => (
            <div
              key={`${item.source}_${item.id}`}
              className="p-4 flex flex-col md:flex-row md:items-center gap-3"
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="font-semibold text-gray-900 dark:text-gray-100 truncate">
                    {item.name}
                  </div>

                  <span
                    className={cx(
                      "inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full",
                      item.status === "active"
                        ? "bg-green-100 text-green-900 dark:bg-green-900/20 dark:text-green-200"
                        : "bg-gray-100 text-gray-900 dark:bg-gray-800 dark:text-gray-200"
                    )}
                  >
                    {item.status === "active" ? (
                      <>
                        <CheckCircle2 className="h-3.5 w-3.5" /> Actif
                      </>
                    ) : (
                      <>
                        <Clock className="h-3.5 w-3.5" /> En attente
                      </>
                    )}
                  </span>

                  <span
                    className={cx("text-xs px-2 py-1 rounded-full", roleBadgeClass(item.role))}
                  >
                    {ROLE_LABEL[item.role]}
                  </span>
                </div>

                <div className="text-sm text-gray-600 dark:text-gray-300 truncate">
                  {item.email}
                </div>

                <div className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                  {item.source === "invitation" ? "Invitation créée le" : "Ajouté le"}{" "}
                  {formatShortDate(item.createdAt)}
                </div>
              </div>

              <div className="flex items-center gap-2">
                {item.status === "pending" && item.source === "invitation" && (
                  <button
                    type="button"
                    onClick={() => handleCopyInviteLink(item)}
                    className="inline-flex items-center gap-2 px-3 py-2 rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 text-sm text-gray-900 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-800"
                  >
                    <Copy className="h-4 w-4" />
                    Copier lien
                  </button>
                )}

                {canRemoveItem(item) && (
                  <button
                    type="button"
                    onClick={() => handleRemove(item)}
                    className="inline-flex items-center gap-2 px-3 py-2 rounded-xl border border-red-200 dark:border-red-900/40 bg-red-50 text-red-700 dark:bg-red-900/10 dark:text-red-200 text-sm hover:bg-red-100 dark:hover:bg-red-900/20"
                  >
                    <Trash2 className="h-4 w-4" />
                    {item.source === "invitation" ? "Supprimer" : "Retirer"}
                  </button>
                )}
              </div>
            </div>
          ))}

          {filtered.length === 0 && (
            <div className="p-6 text-sm text-gray-500 dark:text-gray-400">
              Aucun membre ni invitation ne correspond à ta recherche.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}